import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import RotateLeftOutlinedIcon from "@mui/icons-material/RotateLeftOutlined";
import RotateRightOutlinedIcon from "@mui/icons-material/RotateRightOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import { Box, Button, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { formatImageFileInfo, readImageExif } from "./imageExif";

const imageViewerBackground = "#000";
const imageViewerBorderColor = "#333";

const imageViewerContainerSx = {
  width: "100%",
  borderRadius: 1,
  border: `1px solid ${imageViewerBorderColor}`,
  backgroundColor: imageViewerBackground,
  overflow: "hidden",
};

const imageViewerToolbarSx = {
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  py: 1.25,
  backgroundColor: imageViewerBackground,
  borderTop: `1px solid ${imageViewerBorderColor}`,
};

const imageViewerControlButtonSx = {
  color: "grey.300",
  borderColor: "#444",
  backgroundColor: "transparent",
  "& .MuiButton-startIcon": {
    color: "inherit",
  },
  "&:hover": {
    color: "grey.100",
    borderColor: "#666",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
  "&.Mui-focusVisible": {
    color: "grey.100",
    borderColor: "#888",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
  },
};

const imageViewerFileInfoOverlaySx = {
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 2,
  maxWidth: "min(420px, 55%)",
  maxHeight: "calc(100% - 16px)",
  overflow: "auto",
  pointerEvents: "auto",
  fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
  fontSize: "10px",
  lineHeight: 1.45,
  color: "#c8f05a",
  backgroundColor: "rgba(0, 0, 0, 0.78)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  borderRadius: "2px",
  px: 1,
  py: 0.75,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45)",
};

function ProductImageViewer({ src, alt, maxHeight = 320, minHeight }) {
  const viewerMinHeight = minHeight ?? 160;
  const usesFixedViewerHeight = minHeight != null;
  const [rotation, setRotation] = useState(0);
  const [showFileInfo, setShowFileInfo] = useState(false);
  const [fileInfoText, setFileInfoText] = useState("");
  const [fileInfoLoading, setFileInfoLoading] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  const rotateLeft = () => setRotation((prev) => prev - 90);
  const rotateRight = () => setRotation((prev) => prev + 90);

  const loadFileInfo = useCallback(async () => {
    if (!src) {
      setFileInfoText(formatImageFileInfo({ src, error: "No image source available." }));
      return;
    }

    setFileInfoLoading(true);
    setFileInfoText("Reading EXIF...");

    const { exif, error } = await readImageExif(src);
    setFileInfoText(
      formatImageFileInfo({
        src,
        naturalWidth: naturalSize.width,
        naturalHeight: naturalSize.height,
        exif,
        error,
      })
    );
    setFileInfoLoading(false);
  }, [src, naturalSize.width, naturalSize.height]);

  useEffect(() => {
    setShowFileInfo(false);
    setFileInfoText("");
    setFileInfoLoading(false);
    setNaturalSize({ width: 0, height: 0 });
  }, [src]);

  useEffect(() => {
    if (!showFileInfo) {
      return;
    }

    loadFileInfo();
  }, [showFileInfo, loadFileInfo]);

  const toggleFileInfo = () => {
    setShowFileInfo((prev) => !prev);
  };

  return (
    <TransformWrapper
      minScale={0.5}
      maxScale={5}
      limitToBounds={false}
      smooth
      wheel={{ step: 0.001, excluded: ["product-image-viewer-file-info"] }}
      trackPadPanning={{ excluded: ["product-image-viewer-file-info"] }}
      pinch={{ step: 5 }}
      zoomAnimation={{ size: 0 }}
      doubleClick={{ mode: "zoomIn", step: 0.3 }}
    >
      {({ zoomIn, zoomOut, resetTransform }) => (
        <Box
          sx={imageViewerContainerSx}
          onWheel={(event) => {
            event.stopPropagation();
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              minHeight: viewerMinHeight,
              ...(usesFixedViewerHeight ? { height: viewerMinHeight } : {}),
              maxHeight,
              overflow: "hidden",
              backgroundColor: imageViewerBackground,
            }}
          >
            {showFileInfo ? (
              <Box
                component="pre"
                className="product-image-viewer-file-info"
                sx={imageViewerFileInfoOverlaySx}
                onWheel={(event) => {
                  event.stopPropagation();
                }}
              >
                {fileInfoLoading ? "Reading EXIF..." : fileInfoText}
              </Box>
            ) : null}
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{ width: "100%", height: "100%" }}
            >
              <Box
                component="img"
                src={src}
                alt={alt}
                onLoad={(event) => {
                  const image = event.currentTarget;
                  setNaturalSize({
                    width: image.naturalWidth || 0,
                    height: image.naturalHeight || 0,
                  });
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  maxHeight,
                  objectFit: "contain",
                  transform: `rotate(${rotation}deg)`,
                  transformOrigin: "center center",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
                draggable={false}
              />
            </TransformComponent>
          </Box>

          <Stack direction="row" sx={imageViewerToolbarSx}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<ZoomInOutlinedIcon />}
              aria-label="Zoom in"
              onClick={() => zoomIn(0.25)}
              sx={imageViewerControlButtonSx}
            >
              Zoom in
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<ZoomOutOutlinedIcon />}
              aria-label="Zoom out"
              onClick={() => zoomOut(0.25)}
              sx={imageViewerControlButtonSx}
            >
              Zoom out
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<RotateLeftOutlinedIcon />}
              aria-label="Rotate left"
              onClick={rotateLeft}
              sx={imageViewerControlButtonSx}
            >
              Rotate left
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<RotateRightOutlinedIcon />}
              aria-label="Rotate right"
              onClick={rotateRight}
              sx={imageViewerControlButtonSx}
            >
              Rotate right
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<InfoOutlinedIcon />}
              aria-label="File info"
              aria-pressed={showFileInfo}
              onClick={toggleFileInfo}
              sx={{
                ...imageViewerControlButtonSx,
                ...(showFileInfo
                  ? {
                      color: "grey.100",
                      borderColor: "#888",
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                    }
                  : {}),
              }}
            >
              File info
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<RestartAltOutlinedIcon />}
              aria-label="Reset view"
              onClick={() => {
                setRotation(0);
                resetTransform();
              }}
              sx={imageViewerControlButtonSx}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      )}
    </TransformWrapper>
  );
}

export default ProductImageViewer;
